import { isSameDay } from 'date-fns';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import CurrentTimeIndicator from './current-time-indicator';
import TimelineEventCard from './timeline-event-card';
import {
  formatHourLabel,
  getMinutesOfDay,
  layoutDayEvents,
  TIMELINE_COMPACT_EVENT_MIN_HEIGHT,
  TIMELINE_GUTTER_WIDTH,
  TIMELINE_HOUR_HEIGHT,
  TIMELINE_HOURS,
} from './utils';

const strings = STRINGS.guardian.kids;

/** How often the "now" line re-reads the clock. */
const NOW_TICK_MS = 30_000;
/** Hours of context kept above the auto-scroll anchor. */
const SCROLL_CONTEXT_HOURS = 1.5;
const DEFAULT_ANCHOR_MINUTES = 8 * 60;

export type DayTimelineProps = {
  date: Date;
  today: Date;
  /** Events for `date`, already filtered by the active player. */
  events: FamilyEvent[];
  onEventPress: (event: FamilyEvent) => void;
  contentBottomInset: number;
};

/**
 * Hour-by-hour timeline for a single day.
 *
 * Overlapping events are placed side by side (see `layoutDayEvents`), and the view
 * auto-scrolls to the interesting part of the day on open — now, or the first event.
 * The live "now" line only renders when `date` is today (BR-07).
 */
function DayTimeline({ date, today, events, onEventPress, contentBottomInset }: DayTimelineProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const scrollRef = useRef<ScrollView>(null);
  const [isEventsModalVisible, setIsEventsModalVisible] = useState(false);

  const hourHeight = scale(TIMELINE_HOUR_HEIGHT);
  const gutterWidth = scale(TIMELINE_GUTTER_WIDTH);
  const isToday = isSameDay(date, today);

  const [nowMinutes, setNowMinutes] = useState(() => getMinutesOfDay(new Date()));
  // Snapshot taken once so the auto-scroll target doesn't move on every clock tick.
  const [initialNowMinutes] = useState(() => getMinutesOfDay(new Date()));

  useEffect(() => {
    if (!isToday) return;
    setNowMinutes(getMinutesOfDay(new Date()));
    const timer = setInterval(() => setNowMinutes(getMinutesOfDay(new Date())), NOW_TICK_MS);
    return () => clearInterval(timer);
  }, [isToday]);

  const positioned = useMemo(() => layoutDayEvents(events), [events]);

  const scrollTargetY = useMemo(() => {
    const earliestEvent = events.reduce<number | null>(
      (earliest, event) =>
        earliest === null ? event.startMinutes : Math.min(earliest, event.startMinutes),
      null,
    );
    const anchor = isToday ? initialNowMinutes : (earliestEvent ?? DEFAULT_ANCHOR_MINUTES);
    return Math.max(0, (anchor / 60) * hourHeight - hourHeight * SCROLL_CONTEXT_HOURS);
  }, [events, hourHeight, initialNowMinutes, isToday]);

  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      scrollRef.current?.scrollTo({ y: scrollTargetY, animated: false }),
    );
    return () => cancelAnimationFrame(frame);
  }, [scrollTargetY]);

  const showEventsModal = useCallback(() => setIsEventsModalVisible(true), []);
  const hideEventsModal = useCallback(() => setIsEventsModalVisible(false), []);
  const handleModalEventPress = useCallback(
    (event: FamilyEvent) => {
      hideEventsModal();
      onEventPress(event);
    },
    [hideEventsModal, onEventPress],
  );

  const renderTimelineItem = useCallback(
    (item: (typeof positioned)[number]) => {
      const startMinutes = item.kind === 'event' ? item.event.startMinutes : item.startMinutes;
      const endMinutes = item.kind === 'event' ? item.event.endMinutes : item.endMinutes;
      const durationMinutes = endMinutes - startMinutes;
      const minimumHeight =
        item.kind === 'event' && durationMinutes < 60
          ? scale(TIMELINE_COMPACT_EVENT_MIN_HEIGHT)
          : scale(28);
      const positionStyle: ViewStyle = {
        top: (startMinutes / 60) * hourHeight,
        height: Math.max((durationMinutes / 60) * hourHeight, minimumHeight),
        left: `${(item.columnIndex / item.columnCount) * 100}%`,
        width: `${(1 / item.columnCount) * 100}%`,
      };

      if (item.kind === 'overflow') {
        return (
          <Stack key={item.id} style={[styles.eventBlock, positionStyle]}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={strings.viewMoreDayEvents(item.hiddenCount)}
              onPress={showEventsModal}
              style={({ pressed }) => [styles.overflowButton, pressed ? styles.pressed : null]}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                style={styles.overflowText}>
                {strings.moreDayEvents(item.hiddenCount)}
              </Text>
            </Pressable>
          </Stack>
        );
      }

      return (
        <Stack key={item.event.id} style={[styles.eventBlock, positionStyle]}>
          <TimelineEventCard event={item.event} onPress={onEventPress} />
        </Stack>
      );
    },
    [
      hourHeight,
      onEventPress,
      scale,
      showEventsModal,
      styles.eventBlock,
      styles.overflowButton,
      styles.overflowText,
      styles.pressed,
    ],
  );

  return (
    <ScrollView
      ref={scrollRef}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingTop: scale(10), paddingBottom: contentBottomInset }}>
      <Stack>
        {TIMELINE_HOURS.map(hour => (
          <Stack key={hour} direction="row" height={hour === 24 ? scale(28) : hourHeight}>
            <Stack width={gutterWidth} align="flex-end" paddingRight={scale(8)}>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
                style={styles.hourLabel}>
                {formatHourLabel(hour)}
              </Text>
            </Stack>
            <Stack flex={1} style={styles.hourLine} />
          </Stack>
        ))}

        <Stack style={[styles.eventsLayer, { left: gutterWidth }]}>
          {positioned.map(renderTimelineItem)}
        </Stack>

        {isToday ? <CurrentTimeIndicator minutes={nowMinutes} hourHeight={hourHeight} /> : null}
      </Stack>

      <SheetModal open={isEventsModalVisible} onOpenChange={setIsEventsModalVisible}>
        <SheetContent closeOnBackdropPress enableDragToClose={false} style={styles.modalSheet}>
          <Stack direction="row" align="center" justify="center" paddingX={scale(16)}>
            <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.modalTitle}>
              {strings.dayEventsTitle(events.length)}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={strings.closeDayEvents}
              hitSlop={scale(8)}
              onPress={hideEventsModal}
              style={({ pressed }) => [styles.closeButton, pressed ? styles.pressed : null]}>
              <SvgIcon name="IcXClose" size={scale(24)} color={guardianColors['10']} />
            </Pressable>
          </Stack>

          <ScrollView
            style={styles.modalList}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.modalListContent}>
            {events.map(event => (
              <Stack key={event.id} height={scale(72)}>
                <TimelineEventCard event={event} surface="modal" onPress={handleModalEventPress} />
              </Stack>
            ))}
          </ScrollView>
        </SheetContent>
      </SheetModal>
    </ScrollView>
  );
}

const useStyles = createStyles(({ scale }) => ({
  eventBlock: {
    position: 'absolute',
    paddingRight: scale(6),
  },
  overflowButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors['0'],
    borderRadius: scale(8),
    borderCurve: 'continuous',
    backgroundColor: guardianColors['Main/10'],
  },
  overflowText: {
    color: guardianColors['Main/Main'],
    textAlign: 'center',
  },
  pressed: {
    opacity: 0.76,
  },
  hourLabel: {
    color: guardianColors['6'],
    marginTop: -7,
  },
  hourLine: {
    borderTopWidth: 1,
    borderTopColor: guardianColors['3'],
  },
  eventsLayer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
  },
  modalSheet: {
    maxHeight: '70%',
    paddingTop: scale(16),
    borderTopLeftRadius: scale(16),
    borderTopRightRadius: scale(16),
  },
  modalTitle: {
    color: guardianColors['10'],
  },
  closeButton: {
    position: 'absolute',
    right: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalList: {
    flexShrink: 1,
  },
  modalListContent: {
    gap: scale(8),
    paddingTop: scale(12),
    paddingHorizontal: scale(16),
  },
}));

export default memo(DayTimeline);
